import React, { useContext, useMemo, useState } from "react";
import "./ProductListRowCard.css";
import { useSnackbar } from "../../layouts/ecommerce/product/hooks/SnackbarProvider";
import { UserContext } from "../../App";
import vegIcon from "../../assets/images/veg-new.png";
import nonVegIcon from "../../assets/images/non-veg-icon.svg";
import { Modal, Box, CircularProgress } from "@mui/material";

const isItemAvailable = (itemAvailabilities) => {
  if (!itemAvailabilities || itemAvailabilities.length === 0) return true;
  const now = new Date();
  const cur = now.getHours() * 60 + now.getMinutes();
  return itemAvailabilities.some(({ startTime, endTime }) => {
    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);
    return cur >= sh * 60 + sm && cur <= eh * 60 + em;
  });
};

const getNotServingLabel = (itemAvailabilities) => {
  if (!itemAvailabilities?.length) return "Not serving now";
  const slot = itemAvailabilities[0];
  if (!slot?.endTime) return "Not serving now";
  const fmt = (t) => {
    const [h, m] = t.split(":").map(Number);
    const ap = h >= 12 ? "PM" : "AM";
    return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ap}`;
  };
  return `Not serving now · menu until ${fmt(slot.endTime)}`;
};

const formatAvailabilityTimes = (itemAvailabilities) => {
  if (!itemAvailabilities?.length) return "";
  return itemAvailabilities.map(({ startTime, endTime }) => `${startTime} - ${endTime}`).join(", ");
};

const getRestrictOosConfig = () => {
  try {
    const appConfig = JSON.parse(localStorage.getItem("appConfig") || "{}");
    const flag = appConfig?.all?.RESTRICT_OOS_ORDERING?.flag;
    return flag === false ? false : true;
  } catch {
    return true;
  }
};

const ProductListRowCard = ({
  title,
  price,
  img,
  alt,
  onClick,
  onTileClick,
  loading,
  shortCode,
  foodType,
  complimentaryMode,
  isBundle,
  bundleVariants,
  itemAvailabilities,
  hasMultipleVariants,
  isOutOfStock,
}) => {
  const showSnackbar = useSnackbar();
  const { color } = useContext(UserContext);
  const retailType = localStorage.getItem("retailType");
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const isAvailable = useMemo(() => isItemAvailable(itemAvailabilities), [itemAvailabilities]);
  const notServingLabel = useMemo(
    () => getNotServingLabel(itemAvailabilities),
    [itemAvailabilities]
  );
  const availabilityText = useMemo(
    () => formatAvailabilityTimes(itemAvailabilities),
    [itemAvailabilities]
  );
  const restrictOos = useMemo(() => getRestrictOosConfig(), []);

  const handleAdd = (e) => {
    e.stopPropagation();
    if (complimentaryMode) {
      showSnackbar("Please disable the complimentary mode", "warning");
      return;
    }
    const cartId = localStorage.getItem("CartId");
    if (!cartId) {
      showSnackbar("Please 'ADD' or 'SKIP' customer to add products", "error");
      return;
    }
    if (restrictOos) {
      if (isOutOfStock) {
        showSnackbar("This item is currently out of stock", "warning");
        return;
      }
      if (!isAvailable) {
        setShowConfirmModal(true);
        return;
      }
    }
    onClick();
  };

  const foodIcon =
    foodType === "VEG" || foodType === "VEGAN"
      ? vegIcon
      : foodType === "NON_VEG" || foodType === "EGG"
      ? nonVegIcon
      : null;

  return (
    <>
      <div
        className={`plrc-card ${color === "dark" ? "plrc-dark" : ""}${
          isOutOfStock && restrictOos && !onTileClick ? " plrc-card-disabled" : ""
        }`}
        onClick={onTileClick ?? undefined}
        style={onTileClick ? { cursor: "pointer" } : undefined}
      >
        {/* Thumbnail */}
        <div className="plrc-img-wrap">
          <img
            src={img}
            alt={alt || title}
            className="plrc-img"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src =
                "https://tse4.mm.bing.net/th?id=OIP.mPwk4FItjNeb2Oaq8F1u9QHaGE&pid=Api&P=0";
            }}
          />
          {loading && (
            <div className="plrc-img-loader">
              <CircularProgress size={16} sx={{ color: "#0860E6" }} />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="plrc-content">
          <p className="plrc-name">{title || "—"}</p>

          <div className="plrc-meta">
            {/* Veg / Non-veg icon */}
            {retailType === "RESTAURANT" && foodIcon && (
              <img src={foodIcon} alt={foodType} className="plrc-food-icon" title={foodType} />
            )}

            {/* Price or More Variants */}
            {hasMultipleVariants ? (
              <span className="plrc-badge plrc-badge--variants">More Variants</span>
            ) : price ? (
              <span className="plrc-price">{typeof price === "number" ? `₹${price}` : price}</span>
            ) : null}

            {/* Out of stock */}
            {isOutOfStock && <span className="plrc-badge plrc-badge--oos">Out of stock</span>}

            {/* Not serving now */}
            {!isOutOfStock && !isAvailable && (
              <span className="plrc-badge plrc-badge--unavail">{notServingLabel}</span>
            )}

            {/* Short code */}
            {shortCode && <span className="plrc-shortcode">[{shortCode}]</span>}
          </div>

          {/* Bundle label */}
          {isBundle && bundleVariants?.length > 0 && (
            <p className="plrc-includes-text">Includes {bundleVariants.length} items</p>
          )}
        </div>

        {/* Add button */}
        <button
          className={`plrc-add-btn${isOutOfStock && restrictOos ? " plrc-add-btn--disabled" : ""}`}
          onClick={handleAdd}
          onKeyDown={(e) => {
            if (e.repeat && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
            }
          }}
          disabled={isOutOfStock && restrictOos}
          aria-label={`Add ${title}`}
        >
          +
        </button>
      </div>

      {/* Confirm modal for not-available items */}
      <Modal open={showConfirmModal} onClose={() => setShowConfirmModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            maxWidth: "90%",
            bgcolor: color === "dark" ? "#353a50" : "white",
            color: color === "dark" ? "white" : "black",
            boxShadow: 24,
            borderRadius: "8px",
            p: 3,
          }}
        >
          <h3 style={{ marginTop: 0, marginBottom: "1rem", fontSize: "1.25rem" }}>
            Item Not Available
          </h3>
          <p style={{ marginBottom: "0.5rem", fontSize: "0.938rem" }}>
            <strong>{title}</strong> is not available at this time.
          </p>
          <p style={{ marginBottom: "1.5rem", fontSize: "0.875rem", color: "#ff3b30" }}>
            Available during: {availabilityText}
          </p>
          <p style={{ marginBottom: "1.5rem", fontSize: "0.938rem" }}>
            Do you want to proceed adding this product to the cart?
          </p>
          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
            <button
              onClick={() => setShowConfirmModal(false)}
              style={{
                padding: "10px 20px",
                backgroundColor: color === "dark" ? "#48484a" : "#e0e0e0",
                color: color === "dark" ? "white" : "black",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "0.938rem",
                fontWeight: 600,
              }}
            >
              No
            </button>
            <button
              onClick={() => {
                setShowConfirmModal(false);
                onClick();
              }}
              style={{
                padding: "10px 20px",
                backgroundColor: "#0860E6",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "0.938rem",
                fontWeight: 600,
              }}
            >
              Yes, Add Anyway
            </button>
          </div>
        </Box>
      </Modal>
    </>
  );
};

export default ProductListRowCard;
